import { 
  User, 
  Stethoscope, 
  Home, 
  Car, 
  Briefcase, 
  GraduationCap, 
  Landmark,
  LucideIcon
} from "lucide-react";

export interface DocumentConfig {
  key: string;
  doc_type: string;
  label: string;
  description: string;
  isEssential: boolean;
  expiresShort?: boolean; // True pour justificatifs < 3 mois
  contextual?: {
    minAge?: number;
    maxAge?: number;
    memberTypes?: ('enfant' | 'conjoint' | 'parent' | 'autre')[];
  };
  keywords: string[];
}

export interface CategoryConfig {
  key: string;
  label: string;
  icon: LucideIcon;
  documents: DocumentConfig[];
}

// Configuration centralisée des 7 catégories - "Golden List"
export const ESSENTIAL_DOCS_CONFIG: Record<string, CategoryConfig> = {
  identity: {
    key: "identity",
    label: "Identité",
    icon: User,
    documents: [
      {
        key: "identity_cni",
        doc_type: "identity",
        label: "Carte nationale d'identité",
        description: "CNI en cours de validité",
        isEssential: false,
        keywords: ["cni", "carte identité", "carte d'identité", "carte nationale", "pièce d'identité"],
      },
      {
        key: "identity_passeport",
        doc_type: "identity",
        label: "Passeport",
        description: "Passeport en cours de validité",
        isEssential: false,
        keywords: ["passeport", "passport", "international"],
      },
      {
        key: "identity_titre_sejour",
        doc_type: "identity",
        label: "Titre de séjour",
        description: "Titre de séjour ou visa en cours de validité",
        isEssential: false,
        keywords: ["titre séjour", "visa", "residence permit", "étranger"],
      },
      {
        key: "identity_livret_famille",
        doc_type: "certificate",
        label: "Livret de famille",
        description: "Livret de famille à jour",
        isEssential: true,
        keywords: ["livret", "famille", "family", "mariage", "naissance"],
      },
      {
        key: "identity_acte_naissance",
        doc_type: "certificate",
        label: "Acte de naissance",
        description: "Copie intégrale ou extrait avec filiation",
        isEssential: false,
        keywords: ["naissance", "acte", "birth", "certificate", "état civil"],
      },
    ],
  },
  health: {
    key: "health",
    label: "Santé",
    icon: Stethoscope,
    documents: [
      {
        key: "health_carte_vitale",
        doc_type: "health",
        label: "Carte Vitale",
        description: "Carte d'assurance maladie",
        isEssential: true,
        keywords: ["vitale", "carte vitale", "cpam", "ameli", "assurance maladie", "sécurité sociale"],
      },
      {
        key: "health_mutuelle",
        doc_type: "insurance",
        label: "Attestation Mutuelle",
        description: "Attestation de mutuelle complémentaire",
        isEssential: true,
        keywords: ["mutuelle", "complémentaire", "santé", "tiers payant"],
      },
      {
        key: "health_carnet_sante",
        doc_type: "health",
        label: "Carnet de santé / Vaccins",
        description: "Carnet de santé et vaccinations",
        isEssential: false,
        contextual: { maxAge: 25 },
        keywords: ["vaccin", "vaccination", "carnet", "santé", "rappel"],
      },
      {
        key: "health_ordonnances",
        doc_type: "health",
        label: "Ordonnances",
        description: "Ordonnances médicales en cours",
        isEssential: false,
        keywords: ["ordonnance", "prescription", "médecin", "traitement"],
      },
    ],
  },
  housing: {
    key: "housing",
    label: "Logement",
    icon: Home,
    documents: [
      {
        key: "housing_justificatif_domicile",
        doc_type: "invoice",
        label: "Justificatif de domicile",
        description: "Facture EDF/Eau/Téléphone de moins de 3 mois",
        isEssential: true,
        expiresShort: true,
        keywords: ["domicile", "justificatif", "adresse", "facture", "edf", "engie", "eau", "téléphone"],
      },
      {
        key: "housing_assurance",
        doc_type: "insurance",
        label: "Assurance habitation",
        description: "Attestation d'assurance habitation en cours",
        isEssential: true,
        keywords: ["habitation", "home insurance", "mrh", "multirisque", "locataire"],
      },
      {
        key: "housing_taxe_fonciere",
        doc_type: "tax",
        label: "Taxe foncière / Habitation",
        description: "Dernier avis de taxe foncière ou habitation",
        isEssential: false,
        keywords: ["taxe", "foncière", "habitation", "impôt local"],
      },
      {
        key: "housing_quittance",
        doc_type: "invoice",
        label: "Quittance de loyer",
        description: "Dernière quittance de loyer",
        isEssential: false,
        keywords: ["quittance", "loyer", "rent", "bailleur"],
      },
    ],
  },
  vehicle: {
    key: "vehicle",
    label: "Véhicule",
    icon: Car,
    documents: [
      {
        key: "vehicle_permis",
        doc_type: "identity",
        label: "Permis de conduire",
        description: "Permis de conduire valide",
        isEssential: false,
        contextual: { minAge: 18 },
        keywords: ["permis", "conduire", "driving", "license"],
      },
      {
        key: "vehicle_carte_grise",
        doc_type: "gray_card",
        label: "Carte grise",
        description: "Certificat d'immatriculation du véhicule",
        isEssential: false,
        keywords: ["carte grise", "immatriculation", "certificat", "véhicule"],
      },
      {
        key: "vehicle_assurance",
        doc_type: "insurance",
        label: "Assurance auto",
        description: "Attestation d'assurance automobile en cours",
        isEssential: true,
        keywords: ["assurance auto", "automobile", "véhicule assuré", "responsabilité civile"],
      },
      {
        key: "vehicle_ct",
        doc_type: "certificate",
        label: "Contrôle technique",
        description: "Rapport de contrôle technique valide",
        isEssential: false,
        keywords: ["contrôle", "technique", "ct", "visite"],
      },
    ],
  },
  professional: {
    key: "professional",
    label: "Pro & Revenus",
    icon: Briefcase,
    documents: [
      {
        key: "pro_avis_imposition",
        doc_type: "tax",
        label: "Avis d'imposition",
        description: "Dernier avis d'imposition sur le revenu",
        isEssential: true,
        contextual: { minAge: 18 },
        keywords: ["avis", "imposition", "impôt", "tax", "revenus"],
      },
      {
        key: "pro_bulletins_salaire",
        doc_type: "payslip",
        label: "3 derniers bulletins de salaire",
        description: "Bulletins de paie récents",
        isEssential: false,
        contextual: { minAge: 16 },
        keywords: ["bulletin", "salaire", "paie", "fiche de paie"],
      },
      {
        key: "pro_contrat_travail",
        doc_type: "contract",
        label: "Contrat de travail",
        description: "Contrat de travail actuel",
        isEssential: false,
        contextual: { minAge: 16 },
        keywords: ["contrat", "travail", "cdi", "cdd", "employment"],
      },
      {
        key: "pro_justif_retraite_chomage",
        doc_type: "certificate",
        label: "Justificatif retraite / Chômage",
        description: "Attestation Pôle Emploi ou caisse de retraite",
        isEssential: false,
        keywords: ["retraite", "chômage", "pôle emploi", "pension", "allocation"],
      },
    ],
  },
  education: {
    key: "education",
    label: "Scolarité",
    icon: GraduationCap,
    documents: [
      {
        key: "education_certificat_scolarite",
        doc_type: "certificate",
        label: "Certificat de scolarité",
        description: "Attestation d'inscription de l'année en cours",
        isEssential: false,
        contextual: { minAge: 3, maxAge: 25 },
        keywords: ["scolarité", "certificat", "inscription", "école", "collège", "lycée", "université"],
      },
      {
        key: "education_assurance_scolaire",
        doc_type: "insurance",
        label: "Assurance scolaire",
        description: "Attestation d'assurance scolaire",
        isEssential: true,
        contextual: { minAge: 3, maxAge: 25, memberTypes: ['enfant'] },
        keywords: ["assurance", "scolaire", "MAE", "responsabilité civile"],
      },
      {
        key: "education_diplomes",
        doc_type: "certificate",
        label: "Diplômes",
        description: "Diplômes et attestations de réussite",
        isEssential: false,
        keywords: ["diplôme", "brevet", "bac", "baccalauréat", "licence", "master"],
      },
    ],
  },
  banking: {
    key: "banking",
    label: "Banque",
    icon: Landmark,
    documents: [
      {
        key: "banking_rib",
        doc_type: "bank_statement",
        label: "RIB",
        description: "Relevé d'identité bancaire",
        isEssential: true,
        keywords: ["rib", "bancaire", "iban", "bic", "bank"],
      },
    ],
  },
};

// Liste des clés de catégories dans l'ordre d'affichage
export const CATEGORY_ORDER = [
  "identity",
  "health",
  "housing",
  "vehicle",
  "professional",
  "education",
  "banking",
];

// Helper pour récupérer tous les documents essentiels
export const getEssentialDocuments = (): DocumentConfig[] => {
  const essentialDocs: DocumentConfig[] = [];
  CATEGORY_ORDER.forEach(categoryKey => {
    const category = ESSENTIAL_DOCS_CONFIG[categoryKey];
    category.documents.forEach(doc => {
      if (doc.isEssential) {
        essentialDocs.push(doc);
      }
    });
  });
  return essentialDocs;
};

// Helper pour filtrer les documents selon l'âge
export const filterDocumentsByAge = (docs: DocumentConfig[], age: number | null): DocumentConfig[] => {
  if (age === null) return docs;
  
  return docs.filter(doc => {
    if (!doc.contextual) return true;
    const { minAge, maxAge } = doc.contextual;
    if (minAge !== undefined && age < minAge) return false;
    if (maxAge !== undefined && age > maxAge) return false;
    return true;
  });
};

// Calculer l'âge à partir de la date de naissance
export const calculateAge = (birthDate: string | null): number | null => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Total des documents essentiels (pour le score principal)
export const TOTAL_ESSENTIAL_DOCS = getEssentialDocuments().length;
